import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, ArrowRight, Trophy } from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";
import MobilePocketPopup from "@/pages/MobilePocketPopup";
import MissRodriguezCaseStudy from "@/pages/MissRodriguezCaseStudy";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";
const green = "#8DC63F";

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}
function upsertLD(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function MissRodriguezCaseStudyPage() {
  useEffect(() => {
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    const pageUrl = `${origin}/case-studies/miss-rodriguez`;
    document.title =
      "Miss Rodriguez Pest Control — DFW Website Case Study | PlugitAI";
    upsertMeta(
      "description",
      "How PlugitAI built Miss Rodriguez Pest Control for under $2,000 — from ~3 leads/day in the first 10 days to ~16 leads/day with a ~50% close rate. Full Dallas–Fort Worth pest control case study."
    );
    upsertMeta("robots", "index,follow,max-image-preview:large");
    upsertMeta("og:title", "Miss Rodriguez Pest Control — DFW Website Case Study | PlugitAI", true);
    upsertMeta("og:description",
      "Real client, real numbers: under $2,000 to build, ~16 leads/day at steady state, ~50% close rate in DFW pest control.", true);
    upsertMeta("og:type", "article", true);
    upsertMeta("og:url", pageUrl, true);
    upsertMeta("og:image", `${origin}/showcase/miss-rodriguez-home.png`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertCanonical(pageUrl);

    // CaseStudy JSON-LD (Article + specific case-study props) so Google can
    // surface this as a rich result for "pest control website case study"
    // + branded queries like "Miss Rodriguez Pest Control".
    upsertLD("ld-mr-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Miss Rodriguez Pest Control — Built for Under $2,000",
      description:
        "How PlugitAI built a live DFW pest-control site for under $2,000 that went from ~3 leads/day to ~16 leads/day with a ~50% close rate.",
      image: [`${origin}/showcase/miss-rodriguez-home.png`, `${origin}/showcase/miss-rodriguez-bugs.png`],
      datePublished: "2026-08-01",
      dateModified: "2026-08-01",
      author: { "@type": "Person", name: "Chris Garcia", alternateName: "Kit" },
      publisher: {
        "@type": "Organization",
        name: "PlugitAI",
        logo: { "@type": "ImageObject", url: `${origin}${BRAND.logos.plugitai}` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
      about: {
        "@type": "LocalBusiness",
        name: "Miss Rodriguez Pest Control",
        image: `${origin}/showcase/miss-rodriguez-home.png`,
        telephone: "+1-469-555-0133",
        address: { "@type": "PostalAddress", addressLocality: "Dallas–Fort Worth", addressRegion: "TX", addressCountry: "US" },
        areaServed: [
          { "@type": "City", name: "Dallas" },
          { "@type": "City", name: "Plano" },
          { "@type": "City", name: "Frisco" },
          { "@type": "City", name: "McKinney" },
          { "@type": "City", name: "Arlington" },
        ],
        description: "Residential + commercial pest control across the Dallas–Fort Worth metroplex.",
      },
    });
    upsertLD("ld-mr-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Case Studies", item: `${origin}/case-studies` },
        { "@type": "ListItem", position: 3, name: "Miss Rodriguez Pest Control", item: pageUrl },
      ],
    });

    return () => {
      ["ld-mr-article", "ld-mr-breadcrumb"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div
      className="min-h-screen w-full pb-24 md:pb-0"
      style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
      data-testid="mr-case-study-page"
    >
      <TopNav />

      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: `radial-gradient(1200px 700px at 20% -10%, ${green}18, transparent 60%),
                       radial-gradient(900px 600px at 90% 20%, ${teal}18, transparent 60%),
                       radial-gradient(1400px 900px at 60% 110%, ${gold}0e, transparent 70%)`,
        }}
      />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-40 pb-6 md:pb-10">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-6" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.textDim }}>Case Studies</span>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Miss Rodriguez</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-4"
               style={{ background: `${green}18`, color: green, border: `1px solid ${green}66` }}>
            <Trophy size={11} /> Real client · real numbers
          </div>
          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.2rem, 5vw, 4.4rem)", color: "white" }}>
            Miss Rodriguez Pest Control —{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${green} 0%, ${gold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              a full case study
            </span>
            .
          </h1>
          <p className="mt-5 max-w-[720px] text-base md:text-lg leading-relaxed" style={{ color: C.textDim }}>
            The full breakdown of a DFW pest-control site we built for <strong className="text-white">under $2,000</strong>, and the lead numbers Clarissa has been running since launch. Everything below — the screenshots, the metrics, the page architecture — is real.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm"
               style={{
                 background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                 color: "white",
                 boxShadow: `0 18px 40px -18px ${teal}aa`,
               }}>
              <Phone size={14} /> Build me one like this — (214) 815-5966 <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Full case study — reuse the same component from the homepage */}
      <MissRodriguezCaseStudy />

      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
